import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowUpTrayIcon,
  PhotoIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

function getUrlFromUploadResult(res) {
  if (!res) return "";
  if (typeof res === "string") return res;
  return res.url || res.downloadURL || res.downloadUrl || res.path || "";
}

export default function HeroImageUploader({
  kind = "hero",
  uploadImage,
  onUploaded,
  disabled = false,
  accept = "image/*",
  t,
}) {
  const inputRef = useRef(null);
  const latestUploadId = useRef(0);

  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [localPreview, setLocalPreview] = useState("");

  // cleanup preview url
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const pickFile = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const startUpload = useCallback(
    async (file) => {
      if (!file || !uploadImage) return;

      // make a local preview immediately
      try {
        if (localPreview) URL.revokeObjectURL(localPreview);
        setLocalPreview(URL.createObjectURL(file));
      } catch {
        // ignore preview failure
      }

      setErr("");
      setUploading(true);
      const myId = ++latestUploadId.current;

      try {
        // extra args are safe even if uploadImage only expects 1 argument
        const res = await uploadImage(file, kind);
        const url = getUrlFromUploadResult(res);

        // if another upload started after this one, ignore this result
        if (myId !== latestUploadId.current) return;

        if (url) onUploaded?.(url);
        else setErr(t?.("uploader.error.noUrl", "Upload finished but no URL returned") || "Upload finished but no URL returned");
      } catch (e) {
        if (myId !== latestUploadId.current) return;
        setErr(e?.message || (t?.("uploader.error.failed", "Upload failed") || "Upload failed"));
      } finally {
        if (myId === latestUploadId.current) setUploading(false);
      }
    },
    [uploadImage, kind, onUploaded, t, localPreview]
  );

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    // reset input so selecting same file again triggers change
    e.target.value = "";
    if (file) startUpload(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || uploading) return;

    const file = e.dataTransfer.files?.[0];
    if (file) startUpload(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || uploading) return;
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onFileChange}
        className="hidden"
        disabled={disabled || uploading}
      />

      <button
        type="button"
        onClick={pickFile}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        disabled={disabled || uploading}
        className={`w-full text-left rounded-2xl border transition-all p-4
          ${disabled ? "opacity-60 cursor-not-allowed" : ""}
          ${uploading ? "bg-gray-50 border-gray-200" : ""}
          ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}
        `}
      >
        <div className="flex items-start gap-3">
          <div
            className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center
              ${isDragging ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600"}
            `}
          >
            {uploading ? (
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
            ) : (
              <ArrowUpTrayIcon className="w-5 h-5" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900">
              {uploading
                ? (t?.("uploader.uploading", "Uploading...") || "Uploading...")
                : (t?.("uploader.title", "Upload an image") || "Upload an image")}
            </div>

            <div className="text-xs text-gray-500 mt-1">
              {uploading
                ? (t?.("uploader.wait", "Please wait, the image will be applied automatically") ||
                    "Please wait, the image will be applied automatically")
                : (t?.("uploader.hint", "Click to choose, or drag and drop an image here") ||
                    "Click to choose, or drag and drop an image here")}
            </div>
          </div>

          <div className="shrink-0">
            {localPreview ? (
              <div className="w-14 h-14 rounded-xl overflow-hidden border border-gray-200 bg-white">
                <img src={localPreview} alt="" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-14 h-14 rounded-xl border border-dashed border-gray-300 bg-white flex items-center justify-center text-gray-400">
                <PhotoIcon className="w-6 h-6" />
              </div>
            )}
          </div>
        </div>
      </button>

      {err ? (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="text-sm text-red-700">{err}</div>
        </div>
      ) : null}
    </div>
  );
}